class CartCanvas {
  constructor() {
    this.lastTrigger = null;
    this.busy = false;
    document.addEventListener('click', this.onClick.bind(this));
    document.addEventListener('submit', this.onSubmit.bind(this));
    document.addEventListener('keydown', this.onKeydown.bind(this));
  }

  get root() { return document.querySelector('[data-cart-canvas]'); }
  get dialog() { return this.root?.querySelector('[data-cart-dialog]'); }

  open(trigger) {
    if (!this.root || !this.dialog) return;

    // Only record the trigger the first time, so re-opening after a refresh
    // does not overwrite the element focus should return to.
    if (trigger || !this.lastTrigger) this.lastTrigger = trigger || document.activeElement;

    const alreadyOpen = this.dialog.open;
    if (!alreadyOpen) this.dialog.showModal();
    this.root.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';

    /**
     * Focus without scrolling.
     *
     * `showModal()` and a plain `focus()` both scroll the page to bring the
     * focused element into view, and `scroll-behavior: smooth` on the root
     * turns that into a visible glide to the top of the page. Adding an item
     * from halfway down a product page therefore jumped the page upward and
     * only then revealed the drawer. Moving focus without scrolling removes
     * the jump entirely.
     */
    if (!alreadyOpen) {
      requestAnimationFrame(() =>
        this.root.querySelector('[data-cart-close]')?.focus({ preventScroll: true })
      );
    }
  }

  close() {
    if (!this.root || !this.dialog) return;
    this.root.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    const finish = () => {
      if (this.dialog.open) this.dialog.close();
      this.lastTrigger?.focus?.();
    };
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ? finish() : setTimeout(finish, 360);
  }

  setLoading(value) {
    this.busy = value;
    const loading = this.root?.querySelector('[data-cart-loading]');
    if (loading) loading.hidden = !value;
  }

  showError(message) {
    const error = this.root?.querySelector('[data-cart-error]');
    if (!error) return;

    // The fallback comes from Liquid, not from here. A string written in this
    // file would stay English on an Italian storefront, and it would be the
    // one sentence the customer sees when something has gone wrong.
    error.textContent = message || error.dataset.cartErrorDefault || '';
    error.hidden = false;
  }

  async refresh() {
    const [response, cartResponse] = await Promise.all([
      fetch(`${window.Shopify.routes.root}?sections=cart-drawer`),
      fetch(`${window.Shopify.routes.root}cart.js`, { headers: { Accept: 'application/json' } })
    ]);
    if (!response.ok) throw new Error('Cart refresh failed');
    if (!cartResponse.ok) throw new Error('Cart count refresh failed');
    const sections = await response.json();
    const cart = await cartResponse.json();
    const html = sections['cart-drawer'];
    if (!html) throw new Error('Cart section missing');
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');

    /**
     * Only the contents are swapped, never the section.
     *
     * Replacing `#shopify-section-cart-drawer` destroyed the <dialog> itself,
     * so an open drawer closed and had to be reopened after the response
     * arrived: the drawer appeared, blinked out, and returned half a second
     * later. Swapping the inner shell keeps the same dialog element open for
     * the whole operation, so the panel never leaves the screen.
     */
    const nextContent = documentFragment.querySelector('[data-cart-content]');
    const currentContent = document.querySelector('[data-cart-content]');
    if (!nextContent || !currentContent) throw new Error('Cart section invalid');
    currentContent.replaceChildren(...nextContent.childNodes);
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', { detail: { root: currentContent } }));
    document.querySelectorAll('[data-cart-trigger]').forEach((trigger) => {
      const count = trigger.querySelector('.site-header__cart-count');

      // The badge is absent at zero and has to be created when the first item
      // arrives, otherwise the count never appears until the next page load.
      if (count) {
        count.textContent = cart.item_count;
        count.hidden = cart.item_count === 0;
      }

      /**
       * The label template comes from Liquid, so the count is announced in the
       * customer's language. Building "Cart, N items" here hardcoded English
       * into a storefront that ships in two languages.
       */
      const template = trigger.dataset.cartLabel;
      if (template) {
        trigger.setAttribute('aria-label', template.replace('%count%', cart.item_count));
      }
    });
  }

  async change(line, quantity) {
    if (this.busy) return;
    this.setLoading(true);
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      if (!response.ok) throw new Error((await response.json()).description);
      await this.refresh();
    } catch (error) {
      this.showError(error.message);
    } finally {
      // Cleared on the success path too, not only on error: leaving `busy`
      // true after a successful update made every later cart operation a
      // no-op until the page was reloaded.
      this.setLoading(false);
    }
  }

  async add(form, submitter) {
    if (this.busy) return;
    this.open(submitter);
    this.setLoading(true);
    if (submitter) submitter.disabled = true;
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      if (!response.ok) throw new Error((await response.json()).description);
      await this.refresh();
    } catch (error) {
      this.showError(error.message);
    } finally {
      this.setLoading(false);
      if (submitter) submitter.disabled = false;
    }
  }

  onClick(event) {
    const trigger = event.target.closest('[data-cart-trigger]');
    if (trigger) { event.preventDefault(); this.open(trigger); return; }
    const close = event.target.closest('[data-cart-close]');
    if (close) {
      // The empty-cart CTA closes the drawer and follows its catalogue link.
      // Only non-links need their default action suppressed.
      if (!close.matches('a[href]')) event.preventDefault();
      this.close();
      return;
    }
    const quantity = event.target.closest('[data-cart-quantity]');
    if (quantity) this.change(Number(quantity.dataset.line), Number(quantity.dataset.cartQuantity));
    if (event.target.matches('[data-cart-dialog]')) this.close();
  }

  onSubmit(event) {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    event.preventDefault();
    this.add(form, event.submitter);
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.root?.classList.contains('is-open')) {
      event.preventDefault();
      this.close();
    }
  }
}

new CartCanvas();
